import { Inject } from '@odinjs/odin';

import { ENDPOINTS } from '@philips-emr/hpms-health-insurance-commons';
import { catchRenderException, HpmsCustomCardApi, HpmsCustomCardEvent, HpmsCustomContentCollapseApi, HpmsCustomContentCollapseEvent, HPMSOnOpen, HPMSOnOpenCollapse, shouldRenderMobile } from '@philips-emr/hpms-health-insurance-components';
import { DeckAPI, FieldAPI, PanelAPI } from '@philips-emr/tws-components';
import { ThemeTonalRange } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnAfterFetch, OnDynamicContentRender, OnMount, OnOpen } from '@philips-emr/tws-core/lib/client';
import { Data } from '@philips-emr/tws-core/lib/component/context/data-context';
import { FetchEvent, MountEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, ModalService } from '@philips-emr/tws-core/lib/services';

import { DescreditedProviderAliases as Aliases, FragmentNames } from '../../codegen';
import NoLoginBaseController from '../no-login-base-controller';

import { BasePath405, BasePathAccreditation, mountCardFragment } from './icons-button-utils';

export interface GeneralParams extends Record<string, Value> {
  qtDaysInfoDiscredited: number,
  ieDiscrProviders: string,
  ieComercializedProducts: string;
  ieTypeOfDescriptionProv: string;
  ieDateShowDiscrProvider: string;
  ieShowEmailDiscrProvider: string;
  ieDatePublishedDiscProvider: string;
}

export interface DiscreditedProvider extends Record<string, Value> {
  nrSeqProviderExcl: number,
  nrSeqProvider: number,
  dtEndProvider: string;
  dtPublished: string;
  nmProviderDesc: string;
  cdProviderDesc: string;
  group: string;
}

export interface CardDetailFull {
  ieType: string;
  nrSeqProvExcl: number;
  nrSeqProvider: number;
  nrSeqProviderSubst: number;
  compactName: string;
  dtEndProv: string;
  dtStartProv: string;
  dtPublished: string;
  dsReason: string;
  nmProvider: string;
  cdProviderDesc: string;
  dsAddress: string;
  legends: ProviderLegends;
}
export interface ProviderLegends extends Record<string, Value> {
  ieNotivisa: string;
  iePosGrad360: string;
  ieResidency: string;
  ieQualiss: string;
  ieACBA: string;
  ieAdicq: string;
  ieAIQG: string;
  ieAONA: string;
  ieApalc: string;
  ieSpecialist: string;
  ieAccreditation: string;
  ieISO: string;
  ieOtherQualif: string;
  ieDoctorate: string;
  ieMasters: string;
}

export interface LabelType {
  ieAcredRn405: string;
}

const YES = 'S';
export const SUBSTITUTE = 'S';
const NO = 'N';
export const DISCREDITED = 'D';

@Controller({ fragment: FragmentNames.DESCREDITED_PROVIDER })
export default class DescreditedProviderController extends NoLoginBaseController {
  private legendsBasePath: string;
  private generalParams: GeneralParams;

  @Inject(Aliases.DECK_0)
  private deck: DeckAPI;

  @Inject(Aliases.PANEL_405)
  private panel405: PanelAPI;

  @Inject(Aliases.PANEL_ACREDITACAO)
  private panelAcred: PanelAPI;

  @OnMount(Aliases.ROOT_COMPONENT)
  onRootMount(): void {
    this.noLoginThemeSetUp();
  }

  @OnMount(Aliases.PANEL_0)
  onMountPanel(): void {
    this.deck.setLoading(true);
    HttpService.get<GeneralParams>(ENDPOINTS.GET.DISCREDITED_PROVIDER_PARAMS)
      .then(response => response.body)
      .then(params => {
        if (params) {
          this.generalParams = params;

          HttpService.post<DiscreditedProvider[]>(ENDPOINTS.POST.DISCREDITED_PROVIDER_LIST, {}, { body: params })
            .then(response => response.body)
            .then(list => {
              if (list) {
                list = list.map(value => {
                  value.group = 'group1';
                  return value;
                });
                this.deck.setData(list);
              }
            }).finally(() => {
              catchRenderException(() => this.deck.setLoading(false));
            });
        }
      }).finally(() => {
        HttpService.get<LabelType>(ENDPOINTS.GET.GUIDE_LEGENDS, {}).then(({ body }) => {
          if (body) {
            this.legendsBasePath = body.ieAcredRn405 === YES ? BasePath405 : BasePathAccreditation;
          }
        }).catch(() => this.legendsBasePath = BasePathAccreditation);
      });
  }

  @HPMSOnOpen(Aliases.HPMS_CUSTOM_CARD_0)
  onOpenCustomCard(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const api = event.target as HpmsCustomCardApi;

    if(api.isCollapsed() || !shouldRenderMobile())return;

    const discreditedProvider = api.getState() as DiscreditedProvider;
    api.setCollapsed(true);
    ModalService.openDefault({
      closeable: true,
      fragment: FragmentNames.DISCREDITED_PROVIDERS_DIALOG,
      title: 'hi.product-information',
      contextData: {
        dataParams: { ...discreditedProvider }, legendsBasePath: this.legendsBasePath, generalParams: { ...this.generalParams },
      },
      theme: ThemeTonalRange.ULTRA_LIGHT,
    });

  }

  @OnMount(Aliases.FIELD_DT_PUBLISHED)
  onPublishedMount(event: MountEvent): void {
    const api = event.target as FieldAPI;
    api.setEntry(YES === this.generalParams.ieDatePublishedDiscProvider ? api.getEntry() : '');
    api.setLabel(YES === this.generalParams.ieDatePublishedDiscProvider ? api.getLabel() : '');
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_D)
  onRenderContentD(event: RenderEvent): void {
    const { render, data } = event;
    const cardDetailFull = (data as CardDetailFull);

    if (cardDetailFull.nrSeqProvider == undefined) return;

    const compactName = `${cardDetailFull.nmProvider} - ${cardDetailFull.cdProviderDesc}`;
    const nrSeqProvider = cardDetailFull.nrSeqProvider;

    mountCardFragment(render, {
      nrSeqProvider, dsReason: cardDetailFull.dsReason, compactName, legendsBasePath: this.legendsBasePath, ...this.generalParams,
    });
  }

  @HPMSOnOpenCollapse(Aliases.HPMS_CUSTOM_CARD_CONTENT_COLLAPE_D)
  onOpenRenderContentD(event: HpmsCustomContentCollapseEvent<Data>): void {
    if (!event || !event.data) {
      return;
    }

    const { data, onOpenRender } = event;

    if (shouldRenderMobile()) return;

    onOpenRender({ parameters: { nrSeqProviderExcl: data.nrSeqProviderExcl as string, ieType: DISCREDITED } });
  }

  @OnAfterFetch(Aliases.HPMS_CUSTOM_CARD_CONTENT_COLLAPE_D)
  onAfterFetchContentD(event: FetchEvent): void {
    const api = event.target as HpmsCustomContentCollapseApi;
    const data = event.data as CardDetailFull;
    const { ieType } = data;
    if (ieType !== DISCREDITED) api.setVisible(false);
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_S)
  onDynamicContentRenderS(event: RenderEvent): void {
    const { render, data } = event;
    const cardDetailFull = (data as CardDetailFull);

    if (cardDetailFull.nrSeqProviderSubst == undefined) return;

    const compactName = `${cardDetailFull.nmProvider} - ${cardDetailFull.cdProviderDesc}`;
    const nrSeqProvider = cardDetailFull.nrSeqProviderSubst;
    mountCardFragment(render, {
      nrSeqProvider, compactName, legendsBasePath: this.legendsBasePath, ...this.generalParams,
    });
  }

  @HPMSOnOpenCollapse(Aliases.HPMS_CUSTOM_CARD_CONTENT_COLLAPE_S)
  onOpenRenderContentS(event: HpmsCustomContentCollapseEvent<Data>): void {
    if (!event || !event.data) {
      return;
    }

    if (shouldRenderMobile()) return;

    const { data, onOpenRender } = event;
    onOpenRender({ parameters: { nrSeqProviderExcl: data.nrSeqProviderExcl as string, ieType: SUBSTITUTE } });
  }

  @OnAfterFetch(Aliases.HPMS_CUSTOM_CARD_CONTENT_COLLAPE_S)
  onAfterFetchContentS(event: FetchEvent): void {
    const api = event.target as HpmsCustomContentCollapseApi;
    const data = event.data as CardDetailFull;

    const { ieType } = data;
    if (ieType !== SUBSTITUTE) api.setVisible(false);
  }

  @OnOpen(Aliases.ACCORDION_LEGENDS)
  onLabelsOpen(): void {
    HttpService.get<LabelType>(ENDPOINTS.GET.GUIDE_LEGENDS, {})
      .then(response => response.body)
      .then(labelType => {
        if (labelType) {
          this.panel405.setVisible(labelType.ieAcredRn405 === YES);
          this.panelAcred.setVisible(labelType.ieAcredRn405 === NO);
        }
      }).catch(() => this.panelAcred.setVisible(true));
  }
}
